import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
} from '@floating-ui/react';
import { cn } from '@thinkalpha/ui-core/utils';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { FloatingPane } from 'src/components/ui';

export const JsonFormErrorFloatingPane = ({
    children,
    className,
    errors,
    hasErrors = false,
}: {
    children: ReactNode;
    className?: string;
    errors: ReactNode | string;
    hasErrors: boolean;
}) => {
    const [isHovering, setIsHovering] = useState(false);

    const { x, y, strategy, refs, context } = useFloating({
        placement: 'top-start',
        middleware: [flip(), offset(5)],
        open: isHovering && hasErrors,
        onOpenChange: setIsHovering,
        whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, { enabled: true, delay: { open: 250, close: 100 } });
    const focus = useFocus(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus]);

    return (
        <>
            <div ref={refs.setReference} {...getReferenceProps()} className="w-full">
                {children}
            </div>
            <FloatingPortal>
                <FloatingPane
                    {...getFloatingProps()}
                    ref={refs.setFloating}
                    style={{
                        position: strategy,
                        top: y ?? 0,
                        left: x ?? 0,
                    }}
                    className={cn(
                        className,
                        'max-w-96 rounded-lg p-3 text-sm text-white shadow-md dark:bg-popover',
                        !context.open && 'hidden',
                    )}
                >
                    {errors}
                </FloatingPane>
            </FloatingPortal>
        </>
    );
};
